import type { Flashcard, LearningRank } from '../../api/flashcards'
import { Button } from '../../ui/Control'
import { Preview } from '../../ui/preview/Preview'
import { CardStatus } from './CardStatus'

/**
 * One card as it is shown wherever cards are listed.
 *
 * It lived inside `views/Cards.tsx` and could therefore only be used there, which is why the session
 * of an appointment drew its own cards as a line of plain text: a title, a grey second line, and no
 * sign of which side was which. Two drawings of the same thing is one of them being wrong — and the
 * one in the session was wrong in the way that matters, because a card is the one kind of content
 * whose *shape* is the information. Front and back, side by side, is what says "this is a card".
 *
 * So it is a module of its own now and nothing about it changed. Everything optional stays optional:
 * a caller with no learning history passes no `insights`, and one with nowhere to delete to passes
 * no `onDelete`.
 */
export function CardSummary({
  card,
  answerVisible = true,
  insights,
  tough = false,
  onEdit,
  onDelete,
}: {
  readonly card: Flashcard
  readonly answerVisible?: boolean
  /** One entry per direction. Absent where the state is not the point, as beside a duplicate. */
  readonly insights?: readonly LearningRank[]
  readonly tough?: boolean
  readonly onEdit: () => void
  readonly onDelete?: () => void
}) {
  return (
    <article
      className={`flashcard-summary${answerVisible ? '' : ' flashcard-summary--question-only'}${
        tough ? ' flashcard-summary--needs-work' : ''
      }`}
    >
      <div className="flashcard-summary__side">
        <span>{answerVisible ? 'Front' : 'Question'}</span>
        <small className="flashcard-summary__asked-for">{card.askedFor || '\u00a0'}</small>
        <Preview
          text={card.front}
          format={card.frontFormat}
          label={answerVisible ? 'Front' : 'Question'}
        />
      </div>
      {answerVisible && (
        <div className="flashcard-summary__side">
          <span>Back</span>
          <Preview text={card.back} format={card.backFormat} label="Back" />
        </div>
      )}
      {insights !== undefined && insights.length > 0 && <CardStatus ranks={insights} />}
      <footer>
        <div className="tags">
          {card.tags.map((tag) => (
            <span className="tag" key={tag.id}>
              {tag.name}
            </span>
          ))}
          {card.reversible && <span className="tag tag--direction">Both directions</span>}
        </div>
        <div className="flashcard-summary__actions">
          <Button tone="bare" onClick={onEdit}>
            Edit
          </Button>
          {onDelete !== undefined && (
            <Button tone="bare" className="card-action--danger" onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      </footer>
    </article>
  )
}
